<script setup lang="ts">
import { computed, ref } from 'vue';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/vue/24/solid';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import { useInspectorSections } from '@/composables/useInspectorSections';
import { usePermissions } from '@/composables/usePermissions';
import { formatUsMmDdYyTime12h } from '@/utils/date';
import { QUIET_BUTTON_CLASS } from '@/components/DataTable/quietButton';
import {
    isQueuedContentDelivery,
    useDeleteQueuedCommandMutation,
    useSendQueuedCommandNowMutation,
    type PendingCommandsResponse,
} from '@/composables/api/useLocationZonePlayerInspectorApi';

/**
 * Collapsible "Pending Commands" card for the player inspector — both pending
 * buckets: commands waiting in Redis for the player's next check-in, and queue
 * rows not yet dispatched. Only `queued` rows are deletable (the waiting
 * bucket is the legacy Redis list, which has no per-item delete — that's what
 * clearCommands is for). Open state remembered per entity.
 */
const props = defineProps<{
    waiting: PendingCommandsResponse['waiting'];
    queued: PendingCommandsResponse['queued'];
    isLoading: boolean;
    isError: boolean;
    /** Owner of the pending-commands query the queued-delete mutation patches. */
    locationZonePlayerId: number | null;
}>();

const sections = useInspectorSections('LocationZonePlayer');
const pendingCount = computed(() => props.waiting.length + props.queued.length);

// Visibility mirrors the server's LocationZonePlayerCommandQueuePolicy (the
// policy's model key); the mutation's 403 toast stays as the backstop.
const { can } = usePermissions();
const canDeleteQueuedCommand = computed(() => can('delete', 'location_zone_player_command_queue'));

// "Send now" creates a queue row, so it needs create. Any command but ADDCUSTOM is
// re-created and its old row deleted, so that also needs delete.
const canCreateQueuedCommand = computed(() => can('create', 'location_zone_player_command_queue'));
function canSendNow(command: string): boolean {
    return canCreateQueuedCommand.value && (isQueuedContentDelivery(command) || canDeleteQueuedCommand.value);
}

const sendNowMutation = useSendQueuedCommandNowMutation(computed(() => props.locationZonePlayerId));
const sendingQueuedCommandIds = ref<ReadonlySet<number>>(new Set());
// A content delivery reloads the player's content, so it is confirmed first.
const contentDeliveryAwaitingConfirm = ref<QueuedEntry | null>(null);
const isConfirmOpen = computed({
    get: () => contentDeliveryAwaitingConfirm.value !== null,
    set: (open: boolean) => {
        if (!open) contentDeliveryAwaitingConfirm.value = null;
    },
});

type QueuedEntry = PendingCommandsResponse['queued'][number];

function requestSendNow(entry: QueuedEntry): void {
    if (props.locationZonePlayerId === null || sendingQueuedCommandIds.value.has(entry.id)) {
        return;
    }
    if (isQueuedContentDelivery(entry.command)) {
        contentDeliveryAwaitingConfirm.value = entry;
        return;
    }
    sendNow(entry);
}

function confirmContentDelivery(): void {
    const entry = contentDeliveryAwaitingConfirm.value;
    if (entry) sendNow(entry);
}

function sendNow(entry: QueuedEntry): void {
    sendingQueuedCommandIds.value = new Set([...sendingQueuedCommandIds.value, entry.id]);
    sendNowMutation.mutate(entry, {
        onSettled: () => {
            const remaining = new Set(sendingQueuedCommandIds.value);
            remaining.delete(entry.id);
            sendingQueuedCommandIds.value = remaining;
        },
    });
}

const deleteQueuedCommandMutation = useDeleteQueuedCommandMutation(
    computed(() => props.locationZonePlayerId)
);

// Per-row in-flight tracking: the optimistic cache patch removes the row right
// away, but disable its button anyway so a slow onMutate (query cancellation
// is async) can't double-fire the DELETE.
const deletingQueuedCommandIds = ref<ReadonlySet<number>>(new Set());

function deleteQueuedCommand(queuedCommandId: number): void {
    if (deletingQueuedCommandIds.value.has(queuedCommandId)) {
        return;
    }
    deletingQueuedCommandIds.value = new Set([...deletingQueuedCommandIds.value, queuedCommandId]);
    deleteQueuedCommandMutation.mutate(queuedCommandId, {
        onSettled: () => {
            const remaining = new Set(deletingQueuedCommandIds.value);
            remaining.delete(queuedCommandId);
            deletingQueuedCommandIds.value = remaining;
        },
    });
}
</script>

<template>
    <Collapsible
        :open="sections.isOpen('Pending Commands')"
        class="border-border/80 bg-muted/20 rounded-lg border px-3 py-2"
        data-testid="lzp-inspector-commands"
        @update:open="sections.setOpen('Pending Commands', $event)"
    >
        <CollapsibleTrigger
            class="group focus-visible:ring-ring flex w-full items-center justify-between gap-2 text-left outline-none focus-visible:ring-1"
        >
            <h4 class="text-sm font-semibold">Pending Commands</h4>
            <ChevronDownIcon
                class="text-muted-foreground size-3.5 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180"
                aria-hidden="true"
            />
        </CollapsibleTrigger>
        <CollapsibleContent>
            <p v-if="isLoading" class="text-muted-foreground mt-1 text-xs">Loading…</p>
            <p v-else-if="isError" class="text-destructive mt-1 text-xs">
                Couldn't load pending commands.
            </p>
            <p v-else-if="pendingCount === 0" class="text-muted-foreground mt-1 text-xs">
                No pending commands
            </p>
            <div v-else class="mt-2 flex flex-col gap-3 pb-1">
                <div v-if="waiting.length > 0">
                    <!-- Sentence case, like the sidebar's "Saved views" heading. -->
                    <p class="text-muted-foreground text-xs font-medium" data-testid="lzp-pending-group-heading">
                        Waiting for the player ({{ waiting.length }})
                    </p>
                    <ul class="mt-1 flex flex-col gap-1">
                        <li
                            v-for="(command, index) in waiting"
                            :key="`${index}-${command}`"
                            class="odd:bg-background/80 even:bg-muted/40 border-border/60 truncate rounded border px-2 py-1 font-mono text-xs"
                            :title="command"
                        >
                            {{ command }}
                        </li>
                    </ul>
                </div>
                <div v-if="queued.length > 0">
                    <p class="text-muted-foreground text-xs font-medium" data-testid="lzp-pending-group-heading">
                        Scheduled ({{ queued.length }})
                    </p>
                    <ul class="mt-1 flex flex-col gap-1">
                        <li
                            v-for="entry in queued"
                            :key="entry.id"
                            class="odd:bg-background/80 even:bg-muted/40 border-border/60 flex items-center justify-between gap-3 rounded border py-0.5 pr-1 pl-2"
                        >
                            <span class="truncate font-mono text-xs" :title="entry.command">{{ entry.command }}</span>
                            <span class="flex shrink-0 items-center gap-1">
                                <span class="text-muted-foreground text-[11px] tabular-nums">
                                    {{ formatUsMmDdYyTime12h(entry.scheduled_time ?? entry.created_at) }}
                                </span>
                                <button
                                    v-if="canSendNow(entry.command)"
                                    type="button"
                                    :class="QUIET_BUTTON_CLASS"
                                    aria-label="Send now"
                                    data-testid="lzp-queued-send-now"
                                    :disabled="sendingQueuedCommandIds.has(entry.id) || deletingQueuedCommandIds.has(entry.id)"
                                    @click="requestSendNow(entry)"
                                >
                                    Send now
                                </button>
                                <button
                                    v-if="canDeleteQueuedCommand"
                                    type="button"
                                    class="text-muted-foreground hover:text-destructive hover:bg-muted focus-visible:ring-ring shrink-0 rounded p-0.5 focus-visible:outline-none focus-visible:ring-1 disabled:pointer-events-none disabled:opacity-50"
                                    aria-label="Delete scheduled command"
                                    data-testid="lzp-queued-delete"
                                    :disabled="deletingQueuedCommandIds.has(entry.id) || sendingQueuedCommandIds.has(entry.id)"
                                    @click="deleteQueuedCommand(entry.id)"
                                >
                                    <XMarkIcon class="size-3.5" aria-hidden="true" />
                                </button>
                            </span>
                        </li>
                    </ul>
                </div>
            </div>
        </CollapsibleContent>
        <ConfirmDialog
            v-model:open="isConfirmOpen"
            title="Send the content now?"
            message="The player reloads its content now instead of at 4:00 AM."
            confirm-text="Send now"
            @confirm="confirmContentDelivery"
        />
    </Collapsible>
</template>
